import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { fetchTour, markTourSeen } from "../lib/api";

const PAD = 10;
const CARD_W = 340;

const rectOf = (testid) => {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  return r;
};

const cardPosition = (r, placement) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = r.bottom + 16;
  let left = r.left;
  if (placement === "top") top = Math.max(16, r.top - 190);
  if (placement === "right") {
    top = Math.max(16, r.top);
    left = r.right + 18;
  }
  if (placement === "left") {
    top = Math.max(16, r.top);
    left = Math.max(16, r.left - CARD_W - 18);
  }
  left = Math.min(Math.max(16, left), vw - CARD_W - 16);
  top = Math.min(top, vh - 210);
  return { top, left };
};

/** Numbered walkthrough that points at the real interface. */
export const GuidedTour = ({ steps, audience, onClose }) => {
  const [i, setI] = useState(0);
  const [, force] = useState(0);

  const live = steps.filter((s) => rectOf(s.target));
  const step = live[i];

  useEffect(() => {
    const redraw = () => force((n) => n + 1);
    window.addEventListener("resize", redraw);
    window.addEventListener("scroll", redraw, true);
    const t = setInterval(redraw, 600);
    return () => {
      window.removeEventListener("resize", redraw);
      window.removeEventListener("scroll", redraw, true);
      clearInterval(t);
    };
  }, []);

  const finish = useCallback(() => {
    markTourSeen(audience).catch(() => {});
    onClose();
  }, [audience, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") setI((n) => Math.min(n + 1, live.length - 1));
      if (e.key === "ArrowLeft") setI((n) => Math.max(n - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finish, live.length]);

  if (!step) return null;
  const r = rectOf(step.target);
  if (!r) return null;
  const { top, left } = cardPosition(r, step.placement);
  const dim = "fixed bg-[rgba(2,2,4,0.74)] backdrop-blur-[1px]";

  return (
    <div data-testid="guided-tour" className="pointer-events-none fixed inset-0 z-[90]">
      <div className={dim} style={{ left: 0, top: 0, width: "100%", height: Math.max(0, r.top - PAD) }} />
      <div className={dim} style={{ left: 0, top: r.bottom + PAD, width: "100%", bottom: 0 }} />
      <div className={dim} style={{ left: 0, top: r.top - PAD, width: Math.max(0, r.left - PAD), height: r.height + PAD * 2 }} />
      <div className={dim} style={{ left: r.right + PAD, top: r.top - PAD, right: 0, height: r.height + PAD * 2 }} />

      <div
        className="pointer-events-none fixed border border-zinc-100/70"
        style={{
          left: r.left - PAD,
          top: r.top - PAD,
          width: r.width + PAD * 2,
          height: r.height + PAD * 2,
          boxShadow: "0 0 0 1px rgba(0,0,0,0.6), 0 0 40px rgba(250,250,250,0.12) inset",
        }}
      />

      <div
        data-testid="tour-card"
        className="panel pointer-events-auto fixed p-5"
        style={{ top, left, width: CARD_W }}
      >
        <div className="flex items-center justify-between">
          <span className="label !text-[9px]">
            {audience === "operator" ? "Operator walkthrough" : "Walkthrough"} ·{" "}
            {String(i + 1).padStart(2, "0")} / {String(live.length).padStart(2, "0")}
          </span>
          <button
            data-testid="tour-close-btn"
            onClick={finish}
            className="text-zinc-500 transition-colors hover:text-zinc-100"
          >
            <X size={13} strokeWidth={1.7} />
          </button>
        </div>

        <h3 className="mt-3 font-display text-xl leading-tight">{step.title}</h3>
        <p className="mt-2 text-xs leading-relaxed text-zinc-400">{step.body}</p>

        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid="tour-back-btn"
            disabled={i === 0}
            onClick={() => setI((n) => Math.max(0, n - 1))}
            className="label flex items-center gap-1.5 disabled:opacity-25"
          >
            <ArrowLeft size={11} strokeWidth={1.7} /> back
          </button>
          {i === live.length - 1 ? (
            <button data-testid="tour-done-btn" onClick={finish} className="btn-stark ml-auto">
              Done
            </button>
          ) : (
            <button
              data-testid="tour-next-btn"
              onClick={() => setI((n) => n + 1)}
              className="btn-stark ml-auto flex items-center gap-2"
            >
              next <ArrowRight size={11} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Decides which tour belongs to this route, runs it once, and replays on request. */
export const TourHost = ({ session }) => {
  const loc = useLocation();
  const nav = useNavigate();
  const [run, setRun] = useState(null);
  const isOp = !!session?.user?.is_operator;

  const audience =
    loc.pathname === "/operator" && isOp
      ? "operator"
      : loc.pathname === "/interview"
        ? "subject"
        : null;

  const start = useCallback(
    (want) => {
      fetchTour(want)
        .then((d) => setRun({ audience: want, steps: d.steps }))
        .catch(() => {});
    },
    []
  );

  useEffect(() => {
    if (!audience) return;
    let cancelled = false;
    fetchTour(audience)
      .then((d) => {
        if (cancelled || d.tour_seen) return;
        setTimeout(() => !cancelled && setRun({ audience, steps: d.steps }), 1400);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [audience]);

  useEffect(() => {
    const replay = () => {
      if (audience) return start(audience);
      const want = isOp ? "operator" : "subject";
      nav(want === "operator" ? "/operator" : "/interview");
      setTimeout(() => start(want), 1500);
    };
    window.addEventListener("covalence:replay-tour", replay);
    return () => window.removeEventListener("covalence:replay-tour", replay);
  }, [audience, isOp, nav, start]);

  if (!run) return null;
  return (
    <GuidedTour
      steps={run.steps}
      audience={run.audience}
      onClose={() => setRun(null)}
    />
  );
};

export default TourHost;
